import {
  FaMapMarkerAlt,
  FaRegClock,
  FaStar,
  FaPlane,
  FaMountain,
  FaSnowflake,
  FaFlag,
  FaHiking,
  FaUtensils,
  FaBed,
  FaTint
} from "react-icons/fa";
import { IoMdArrowDropright, IoMdCheckmarkCircle } from "react-icons/io";
import { AppContext } from "../../App";
import { useContext } from "react";

import imageHero from "../../assets/kilimanjaro.jpg";
import imageDay1 from "../../assets/kili2.jpg";
import imageDay2 from "../../assets/mtkili.jpg";
import imageDay3 from "../../assets/kili.jpg";
import imageDay4 from "../../assets/mtkili.jpg";
import imageDay5 from "../../assets/kilimanjaro.jpg";
import imageDay6 from "../../assets/summit.jpg";

const hero = {
  kicker: "Machame Route Adventure",
  title: "Scenic Machame Trek",
  subtitle: "Whiskey Route style adventure with lush forests, alpine deserts, and summit deadlines.",
  backgroundImage: imageHero,
  meta: ["7-day profile", "Strong acclimatization", "Guided small groups"],
};

const overview = `The Machame Route on Mount Kilimanjaro is a captivating six-day trek, renowned for its diverse landscapes and excellent acclimatization opportunities. Beginning in the lush rainforests, trekkers navigate through moorlands, alpine deserts, and iconic rock formations. The route presents challenging yet rewarding sections, including the Barranco Wall and the Lava Tower. Culminating at Uhuru Peak, the highest point in Africa at 5,895 meters, the Machame Route offers breathtaking panoramic views of Kilimanjaro's majestic glaciers. With a thoughtfully planned itinerary, this route provides an immersive and memorable adventure for those seeking to conquer the "Roof of Africa."`;

const highlights = [
  {
    icon: FaHiking,
    title: "Rainforest Start",
    description: "Begin at Machame Gate and enjoy lush forest trails with daily acclimatization.",
    color: "#10b981"
  },
  {
    icon: FaMountain,
    title: "Barranco & Karanga",
    description: "Cross the famous Barranco Wall and traverse Karanga Valley for unforgettable panoramas.",
    color: "#3b82f6"
  },
  {
    icon: FaFlag,
    title: "Summit Focus",
    description: "Summit via Barafu Camp at dawn for sunrise over Uhuru Peak.",
    color: "#ef4444"
  },
  {
    icon: FaRegClock,
    title: "Best Season",
    description: "January–March and June–October offer the most stable weather for Machame.",
    color: "#f59e0b"
  },
];

const details = [
  {
    icon: FaMapMarkerAlt,
    title: "Route",
    description: "Machame Gate > Machame Camp > Shira > Barranco > Karanga > Barafu > Uhuru > Mweka.",
  },
  {
    icon: FaRegClock,
    title: "Duration",
    description: "7 days with dedicated acclimatization to maximize summit success.",
  },
  {
    icon: FaBed,
    title: "Accommodation",
    description: "Mountain huts and camping at designated campsites with full support crew.",
  },
  {
    icon: FaTint,
    title: "Hydration",
    description: "Daily water purification and hydration strategy for high-altitude trekking.",
  },
];

const itinerary = [
  {
    dayLabel: "Day 1",
    dayNum: "01",
    title: "Machame Gate to Machame Camp",
    image: imageDay1,
    body: "Embark on your Kilimanjaro adventure by registering at Machame Gate. The trek begins through the enchanting rainforest, offering glimpses of diverse wildlife and lush vegetation. After a few hours of hiking, you'll reach Machame Camp, nestled at an elevation of 2,835 meters. This initial leg provides a gentle introduction to the trek and allows you to acclimate to the changing altitude.",
    activities: "Accommodation: Machame Camp • Elevation: 2,835m",
    tags: ["Rainforest", "Acclimatization", "Wildlife"]
  },
  {
    dayLabel: "Day 2",
    dayNum: "02",
    title: "Machame Camp to Shira Camp",
    image: imageDay2,
    body: "Leaving the rainforest behind, today's journey leads through the expansive Shira Plateau. The scenery transforms into open moorland, providing stunning panoramic views. After ascending to 3,750 meters, you'll reach Shira Camp, where the landscape becomes more rugged, and the air thinner. Take in the beauty of the plateau and rest for the night to prepare for the challenges ahead.",
    activities: "Accommodation: Shira Camp • Elevation: 3,750m",
    tags: ["Moorland", "Panoramas", "Shira Plateau"]
  },
  {
    dayLabel: "Day 3",
    dayNum: "03",
    title: "Shira Camp to Barranco Camp via Lava Tower",
    image: imageDay3,
    body: "Climb to the towering Lava Tower at 4,630 meters for acclimatization, experiencing the alpine desert environment. Descend to Barranco Camp, navigating the iconic Barranco Wall. This section of the trek combines elevation gain with strategic descents, enhancing acclimatization. Barranco Camp, at 3,960 meters, provides a spectacular setting for the night.",
    activities: "Accommodation: Barranco Camp • Elevation: 3,960m",
    tags: ["Lava Tower", "Barranco Wall", "Acclimatization"]
  },
  {
    dayLabel: "Day 4",
    dayNum: "04",
    title: "Barranco Camp to Karanga Camp",
    image: imageDay4,
    body: "Conquer the Barranco Wall, a thrilling highlight, and continue to Karanga Camp. The landscape becomes more rugged, surrounded by giant Senecio plants. Nestled at an altitude of 3,995 meters beneath the Southern Icefield of Kibo, Karanga Camp offers breathtaking views and serves as a crucial resting point before the final ascent.",
    activities: "Accommodation: Karanga Camp • Elevation: 3,995m",
    tags: ["Barranco Wall", "Senecio Plants", "Icefield Views"]
  },
  {
    dayLabel: "Day 5",
    dayNum: "05",
    title: "Karanga Camp to Barafu Camp",
    image: imageDay5,
    body: "Ascend to Barafu Camp, the last base before the summit push. The terrain becomes steeper, and the air thinner as you climb to 4,673 meters. Barafu Camp sits beneath the glaciers of Kibo, providing a dramatic backdrop. Rest and mentally prepare for the challenging summit attempt, appreciating the magnificent sunset over the icy peaks.",
    activities: "Accommodation: Barafu Camp • Elevation: 4,673m",
    tags: ["Summit Prep", "Glacier Views", "High Altitude"]
  },
  {
    dayLabel: "Day 6",
    dayNum: "06",
    title: "Summit Day: Barafu to Uhuru Peak to Mweka",
    image: imageDay6,
    body: "Begin the summit attempt in the early hours, navigating the switchbacks to reach the iconic Uhuru Peak at 5,895 meters in time for a breathtaking sunrise. Descend to Barafu Camp for a brief rest before continuing down to Mweka Camp at 3,100 meters. This final descent offers a well-deserved celebration of your achievements and a night's rest before concluding the trek.",
    activities: "Accommodation: Mweka Camp • Summit: 5,895m",
    tags: ["Uhuru Peak", "Sunrise Summit", "Achievement"]
  },
];

export default function MachameRoute() {

  const appContext = useContext(AppContext);
  
  // Kilimanjaro theme: Deep blues, cool grays, summit red accent
  const theme = {
    primary: "#1e40af",      // Blue-800 (mountain depth)
    primaryLight: "#3b82f6", // Blue-500
    accent: "#dc2626",       // Red-600 (summit achievement)
    bg: "#f8fafc",           // Cool gray background
    cardBg: "#ffffff",
    text: "#1e293b",
    textMuted: "#64748b"
  };

  return (
    <div style={{ 
      maxWidth: "1400px", 
      margin: "0 auto", 
      fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
      color: theme.text,
      background: theme.bg
    }}>
      
      {/* ================= HERO SECTION ================= */}
      <section
        style={{
          height: "75vh",
          minHeight: "500px",
          backgroundImage: `linear-gradient(135deg, rgba(2, 6, 23, 0.95), rgba(30, 64, 175, 0.85)), url(${hero.backgroundImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "white",
          textAlign: "center",
          padding: "0 1.5rem",
          position: "relative"
        }}
      >
        <div style={{ maxWidth: "800px", animation: "fadeInUp 0.8s ease-out" }}>
          <span 
            className="badge rounded-pill px-4 py-2 mb-4"
            style={{ 
              background: "rgba(59,130,246,0.25)", 
              border: "1px solid rgba(59,130,246,0.5)",
              color: "#93c5fd",
              fontSize: "0.9rem",
              fontWeight: "600",
              letterSpacing: "0.5px",
              backdropFilter: "blur(4px)"
            }}
          >
            {hero.kicker}
          </span>
          
          <h1 
            style={{ 
              fontSize: "clamp(2.5rem, 6vw, 4rem)", 
              marginBottom: "1rem", 
              lineHeight: 1.1,
              fontWeight: "700",
              textShadow: "0 2px 15px rgba(0,0,0,0.6)"
            }}
          >
            {hero.title}
          </h1>
          
          <p 
            style={{ 
              fontSize: "clamp(1.1rem, 2.5vw, 1.35rem)", 
              marginBottom: "2.5rem",
              opacity: 0.95,
              lineHeight: 1.6
            }}
          >
            {hero.subtitle}
          </p>
          
          <div style={{ display: "flex", gap: "0.75rem", justifyContent: "center", flexWrap: "wrap" }}>
            {hero.meta.map((m, i) => (
              <span 
                key={i} 
                style={{ 
                  background: "rgba(255,255,255,0.18)", 
                  padding: "0.5rem 1.25rem", 
                  borderRadius: "50px",
                  fontSize: "0.9rem",
                  fontWeight: "500",
                  border: "1px solid rgba(255,255,255,0.25)",
                  backdropFilter: "blur(4px)"
                }}
              >
                {m}
              </span>
            ))}
          </div>
        </div>
      </section>

      <div style={{ padding: "4rem 1.5rem" }}>
        
        {/* ================= OVERVIEW ================= */}
        <section style={{ marginBottom: "5rem", textAlign: "center", maxWidth: "800px", margin: "0 auto 5rem" }}>
          <h2 
            style={{ 
              fontSize: "clamp(1.75rem, 4vw, 2.25rem)", 
              marginBottom: "1.5rem", 
              color: theme.text,
              fontWeight: "700"
            }}
          >
            Conquer the Roof of Africa
          </h2>
          <p 
            style={{ 
              fontSize: "1.1rem", 
              lineHeight: 1.8, 
              color: theme.textMuted,
              marginBottom: "2rem"
            }}
          >
            {overview}
          </p>
          
          {/* Quick Stats */}
          <div className="row g-3 justify-content-center" style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
            {details.map((d, i) => {
              const DIcon = d.icon;
              return (
                <div 
                  key={i}
                  className="col-6 col-md-3"
                  style={{
                    background: theme.cardBg,
                    padding: "1rem 1.5rem",
                    borderRadius: "12px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    minWidth: "200px",
                    border: "1px solid rgba(0,0,0,0.04)"
                  }}
                >
                  <DIcon size={22} style={{ color: theme.primary, flexShrink: 0 }} />
                  <div style={{ textAlign: "left" }}>
                    <div style={{ fontSize: "0.8rem", fontWeight: "600", color: theme.text }}>{d.title}</div>
                    <div style={{ fontSize: "0.85rem", color: theme.textMuted }}>{d.description}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* ================= HIGHLIGHTS GRID ================= */}
        <section style={{ marginBottom: "5rem" }}>
          <div className="text-center mb-4">
            <h2 
              style={{ 
                fontSize: "clamp(1.75rem, 4vw, 2.25rem)", 
                marginBottom: "0.5rem", 
                color: theme.text,
                fontWeight: "700"
              }}
            >
              Trek Highlights
            </h2>
            <p style={{ color: theme.textMuted }}>Key moments on your journey to Uhuru Peak</p>
          </div>
          
          <div style={{ 
            display: "grid", 
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", 
            gap: "1.5rem" 
          }}>
            {highlights.map((h, i) => {
              const HIcon = h.icon;
              return (
                <article 
                  key={i} 
                  style={{ 
                    background: theme.cardBg,
                    padding: "1.75rem",
                    borderRadius: "16px",
                    boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                    cursor: "default",
                    border: "1px solid rgba(0,0,0,0.04)",
                    position: "relative",
                    overflow: "hidden"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-4px)";
                    e.currentTarget.style.boxShadow = "0 12px 28px rgba(0,0,0,0.12)";
                    e.currentTarget.style.borderColor = h.color + "66";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 4px 14px rgba(0,0,0,0.08)";
                    e.currentTarget.style.borderColor = "rgba(0,0,0,0.04)";
                  }}
                >
                  {/* Subtle gradient accent */}
                  <div 
                    className="position-absolute top-0 end-0 opacity-10"
                    style={{
                      width: "100px",
                      height: "100px",
                      background: `radial-gradient(circle at top right, ${h.color}33, transparent 70%)`,
                      borderRadius: "0 0 0 100%"
                    }}
                  />
                  
                  <div 
                    className="d-inline-flex align-items-center justify-content-center rounded-2 mb-3 position-relative"
                    style={{
                      width: "52px",
                      height: "52px",
                      background: `linear-gradient(135deg, ${h.color}20, ${h.color}10)`,
                      color: h.color,
                      fontSize: "1.4rem",
                      border: `1px solid ${h.color}40`
                    }}
                  >
                    <HIcon />
                  </div>
                  <h3 style={{ fontSize: "1.25rem", marginBottom: "0.75rem", fontWeight: "600", color: theme.text, position: "relative" }}>
                    {h.title}
                  </h3>
                  <p style={{ color: theme.textMuted, lineHeight: 1.6, fontSize: "0.95rem", margin: 0, position: "relative" }}>
                    {h.description}
                  </p>
                </article>
              );
            })}
          </div>
        </section>

        {/* ================= ITINERARY TIMELINE ================= */}
        <section>
          <div className="text-center mb-5">
            <h2 
              style={{ 
                fontSize: "clamp(1.75rem, 4vw, 2.25rem)", 
                marginBottom: "0.5rem", 
                color: theme.text,
                fontWeight: "700"
              }}
            >
              Your 7-Day Summit Journey
            </h2>
            <p style={{ color: theme.textMuted }}>Day-by-day ascent through Kilimanjaro's ecological zones</p>
          </div>

          <div style={{ position: "relative" }}>
            {/* Timeline connector line (desktop) */}
            <div 
              className="d-none d-lg-block"
              style={{
                position: "absolute",
                left: "50%",
                top: "0",
                bottom: "0",
                width: "2px",
                background: `linear-gradient(to bottom, ${theme.primary}33, ${theme.accent}33, transparent)`,
                transform: "translateX(-50%)"
              }}
            />

            {itinerary.map((day, i) => (
              <article 
                key={i} 
                className={`row g-4 align-items-center mb-4 ${i % 2 === 0 ? 'flex-lg-row' : 'flex-lg-row-reverse'}`}
                style={{
                  background: theme.cardBg,
                  borderRadius: "20px",
                  overflow: "hidden",
                  boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
                  border: "1px solid rgba(0,0,0,0.04)",
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  position: "relative",
                  zIndex: 1
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-3px)";
                  e.currentTarget.style.boxShadow = "0 14px 35px rgba(0,0,0,0.12)";
                  e.currentTarget.style.borderColor = i === itinerary.length - 1 ? theme.accent + "66" : theme.primary + "66";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.08)";
                  e.currentTarget.style.borderColor = "rgba(0,0,0,0.04)";
                }}
              >
                {/* Day Number Badge - Summit red for final day */}
                <div 
                  className="position-absolute top-0 start-0 m-3 rounded-pill px-3 py-1"
                  style={{
                    background: i === itinerary.length - 1 
                      ? `linear-gradient(135deg, ${theme.accent}, #ef4444)` 
                      : `linear-gradient(135deg, ${theme.primary}, ${theme.primaryLight})`,
                    color: "white",
                    fontWeight: "700",
                    fontSize: "0.85rem",
                    zIndex: 2,
                    boxShadow: i === itinerary.length - 1 
                      ? "0 4px 12px rgba(220, 38, 38, 0.4)" 
                      : "0 4px 12px rgba(30, 64, 175, 0.4)"
                  }}
                >
                  Day {day.dayNum}
                </div>

                {/* Image Column */}
                <div className="col-lg-5" style={{ position: "relative", minHeight: "280px" }}>
                  <div
                    style={{
                      height: "100%",
                      minHeight: "280px",
                      backgroundImage: `url(${day.image})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      transition: "transform 0.5s ease"
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = "scale(1.03)"}
                    onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
                  />
                  {/* Gradient overlay for text readability */}
                  <div 
                    style={{
                      position: "absolute",
                      inset: 0,
                      background: "linear-gradient(to top, rgba(2,6,23,0.8), transparent 60%)"
                    }}
                  />
                </div>

                {/* Content Column */}
                <div className="col-lg-7 p-4 p-lg-5">
                  <h3 
                    style={{ 
                      fontSize: "1.4rem", 
                      marginBottom: "1rem", 
                      fontWeight: "700",
                      color: theme.text,
                      paddingRight: "60px",
                      lineHeight: 1.3
                    }}
                  >
                    {day.title}
                  </h3>
                  
                  <p style={{ lineHeight: 1.7, color: theme.textMuted, marginBottom: "1.25rem", fontSize: "0.95rem" }}>
                    {day.body}
                  </p>
                  
                  {/* Tags */}
                  <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginBottom: "1.25rem" }}>
                    {day.tags.map((tag, idx) => (
                      <span 
                        key={idx}
                        style={{
                          background: `${theme.primary}10`,
                          color: theme.primary,
                          padding: "0.35rem 0.85rem",
                          borderRadius: "50px",
                          fontSize: "0.8rem",
                          fontWeight: "500",
                          border: `1px solid ${theme.primary}25`
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  
                  {/* Elevation/Activities Info */}
                  <div 
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      padding: "0.6rem 1.2rem",
                      background: i === itinerary.length - 1 
                        ? `linear-gradient(135deg, ${theme.accent}15, ${theme.accent}08)`
                        : `linear-gradient(135deg, ${theme.primary}15, ${theme.primary}08)`,
                      borderRadius: "10px",
                      fontSize: "0.9rem",
                      color: i === itinerary.length - 1 ? theme.accent : theme.primary,
                      fontWeight: "500",
                      border: `1px solid ${i === itinerary.length - 1 ? theme.accent : theme.primary}30`
                    }}
                  >
                    {i === itinerary.length - 1 ? <FaFlag size={14} /> : <FaBed size={14} />}
                    {day.activities}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* ================= SUMMIT PREP HIGHLIGHT ================= */}
        <section 
          style={{ 
            background: `linear-gradient(135deg, ${theme.primary}08, ${theme.accent}08)`,
            borderRadius: "20px",
            padding: "2.5rem 2rem",
            marginTop: "3rem",
            border: `1px solid ${theme.primary}20`
          }}
        >
          <div className="text-center mb-4">
            <FaMountain size={36} style={{ color: theme.primary, marginBottom: "1rem" }} />
            <h3 
              style={{ 
                fontSize: "clamp(1.4rem, 3vw, 1.75rem)", 
                fontWeight: "700", 
                marginBottom: "0.75rem",
                color: theme.text
              }}
            >
              Summit Success Essentials
            </h3>
          </div>
          <div className="row g-3 justify-content-center" style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
            {[
              "Certified IFMGA Mountain Guides (1:3 ratio)",
              "Daily Health Checks & Pulse Oximetry Monitoring",
              "High-Altitude Acclimatization Strategy Built-In",
              "Quality Camping Gear & Sleeping Equipment Provided",
              "Nutritious Meals Prepared by Experienced Cooks",
              "Porter Welfare Certified – Ethical Treatment Guaranteed"
            ].map((item, idx) => (
              <div 
                key={idx}
                className="col-12 col-md-6 col-lg-4"
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "0.75rem"
                }}
              >
                <IoMdCheckmarkCircle size={20} style={{ color: theme.primary, flexShrink: 0, marginTop: "2px" }} />
                <span style={{ fontSize: "0.95rem", color: theme.text, lineHeight: 1.5 }}>{item}</span>
              </div>
            ))}
          </div>
        </section>

        {/* ================= CTA SECTION ================= */}
        <section 
          className="text-center mt-6 pt-5"
          style={{ 
            borderTop: `1px solid rgba(0,0,0,0.06)`,
            background: `linear-gradient(135deg, ${theme.primary}08, ${theme.accent}08)`,
            borderRadius: "20px",
            padding: "3rem 2rem",
            marginTop: "4rem"
          }}
        >
          <FaFlag size={42} style={{ color: theme.accent, marginBottom: "1rem" }} />
          <h3 
            style={{ 
              fontSize: "clamp(1.5rem, 4vw, 2rem)", 
              fontWeight: "700", 
              marginBottom: "1rem",
              color: theme.text
            }}
          >
            Ready to Stand on Top of Africa?
          </h3>
          <p style={{ color: theme.textMuted, maxWidth: "600px", margin: "0 auto 2rem", lineHeight: 1.6 }}>
            Secure your Machame Route expedition with our expert guiding team. Limited group sizes for personalized attention. Pre-trek training guidance included.
          </p>
          <button 
            className="btn btn-lg px-5 py-3 rounded-pill"
            style={{ 
              background: `linear-gradient(135deg, ${theme.accent}, #ef4444)`,
              border: "none",
              fontWeight: "600",
              color: "white",
              boxShadow: `0 6px 20px ${theme.accent}40`,
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              transition: "transform 0.2s ease, box-shadow 0.2s ease"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = `0 8px 25px ${theme.accent}50`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = `0 6px 20px ${theme.accent}40`;
            }}
            onClick={() => appContext.navTo({ item: 'plan-trip' })}
          >
            Reserve Your Summit Spot <IoMdArrowDropright size={20} />
          </button>
          <p style={{ fontSize: "0.85rem", color: theme.textMuted, marginTop: "1.5rem", margin: 0 }}>
            <FaRegClock size={14} className="me-1" /> Free pre-trek consultation • Flexible cancellation policy
          </p>
        </section>

      </div>

      {/* ================= CSS ANIMATIONS ================= */}
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          * { transition: none !important; animation: none !important; }
        }
        @media (max-width: 991px) {
          [style*="left: 50%"] { display: none !important; }
        }
      `}</style>
    </div>
  );
}